import {
  Component,
  OnInit,
  OnDestroy,
  ViewEncapsulation,
  ElementRef,
  ViewChild,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  HostListener,
} from "@angular/core";
import { Title } from "@angular/platform-browser";
import { AbstractControl, FormArray, FormBuilder, FormGroup } from "@angular/forms";
import { MatAutocompleteSelectedEvent } from "@angular/material/autocomplete";

import { BehaviorSubject, EMPTY, forkJoin, Observable, of, Subscription } from "rxjs";
import { catchError, finalize, map, shareReplay, skip, tap } from "rxjs/operators";
import { select, Store } from "@ngrx/store";
import { AppState, sitesStateSitesList } from "src/app/store/reducers";
import { getSitesList } from "src/app/store/actions/sites/sites.actions";
import { setSnackbar } from "src/app/store/actions/messages/message.actions";

import { TranslateService } from "@ngx-translate/core";
import { DashboardService } from "src/app/shared/services/dashboard/dashboard.service";

import { AutocompleteSelectItem } from "src/app/models/selection-list/autocomplete-select-item.model";
import { SiteDTO } from "src/app/models/Sites/siteDTO";
import { SnackBarUtils } from "src/app/shared/utils/snackbarUtils";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { BusinessType } from "src/app/models/enums/businessType.enum";
import { RetailDashboardTypes } from "src/app/models/enums/retailDashboardTypes.enum";
import { TenantDataFormatDTO } from "src/app/models/Tenant/Tenant.model";
import { FormatType } from "src/app/models/custom-table/custom-table.model";
import { FormatPipe } from "src/app/shared/pipes/formatPipe";

interface DashboardPayload {
  type: string;
  range: { start: string; end: string };
  filters: { site_number: string[]; is_fuel: boolean };
};

@Component({
  selector: "app-retail-dashboard",
  templateUrl: "./retail-dashboard.component.html",
  styleUrls: ["./retail-dashboard.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class RetailDashboardComponent implements OnInit, OnDestroy {
  @Input() businessTypes: BusinessType[];
  @Input() tenantDataFormat: TenantDataFormatDTO;

  @Output() selectBusiness: EventEmitter<BusinessType> = new EventEmitter<BusinessType>();

  @ViewChild('filterCard', { static: false, read: ElementRef }) filterCard!: ElementRef;
  @ViewChild("chartContainer1", { static: true }) chartContainer1!: ElementRef;
  @ViewChild("chartContainer2", { static: true }) chartContainer2!: ElementRef;
  @ViewChild("chartContainer3", { static: true }) chartContainer3!: ElementRef;
  @ViewChild("chartContainer4", { static: true }) chartContainer4!: ElementRef;
  @ViewChild("chartContainer5", { static: true }) chartContainer5!: ElementRef;
  @ViewChild("chartContainer6", { static: true }) chartContainer6!: ElementRef;

  langSubscription: Subscription;;

  firstLoad = true;

  isLoadingChart1$ = new BehaviorSubject<boolean>(false);
  isLoadingChart2$ = new BehaviorSubject<boolean>(false);
  isLoadingChart3$ = new BehaviorSubject<boolean>(false);
  isLoadingChart4$ = new BehaviorSubject<boolean>(false);
  isLoadingChart5$ = new BehaviorSubject<boolean>(false);
  isLoadingChart6$ = new BehaviorSubject<boolean>(false);

  echarts5: any;

  chart1!: any;
  chart1series!: any;
  chart2!: any;
  chart2series!: any;
  chart3!: any;
  chart3series!: any;
  chart4!: any;
  chart5!: any;
  chart6!: any;

  barChartStatus1 = true;
  barChartStatus2 = true;
  barChartStatus3 = false;
  horizontalBarChartStatus1 = false;
  horizontalBarChartStatus2 = false;
  horizontalBarChartStatus3 = false;
  pieChartStatus1 = false;
  pieChartStatus2 = false;
  pieChartStatus3 = true;
  doughnutChartStatus1 = false;
  doughnutChartStatus2 = false;
  doughnutChartStatus3 = false;

  chartConfig = {
    chart1: {
      series: () => this.chart1series,
      status: {
        bar: (val: boolean) => this.barChartStatus1 = val,
        horizontalBar: (val: boolean) => this.horizontalBarChartStatus1 = val,
        pie: (val: boolean) => this.pieChartStatus1 = val,
        doughnut: (val: boolean) => this.doughnutChartStatus1 = val,
      }
    },
    chart2: {
      series: () => this.chart2series,
      status: {
        bar: (val: boolean) => this.barChartStatus2 = val,
        horizontalBar: (val: boolean) => this.horizontalBarChartStatus2 = val,
        pie: (val: boolean) => this.pieChartStatus2 = val,
        doughnut: (val: boolean) => this.doughnutChartStatus2 = val,
      }
    },
    chart3: {
      series: () => this.chart3series,
      status: {
        bar: (val: boolean) => this.barChartStatus3 = val,
        horizontalBar: (val: boolean) => this.horizontalBarChartStatus3 = val,
        pie: (val: boolean) => this.pieChartStatus3 = val,
        doughnut: (val: boolean) => this.doughnutChartStatus3 = val,
      }
    }
  };

  siteDataForm: FormGroup;
  sitesSub: Subscription;
  sitesList$: Observable<SiteDTO[]>;
  sitesListData = [];
  loadingSearch = false;
  searchTimer: number;

  dashboardPayload: DashboardPayload = {
    type: "",
    range: {
      start: "",
      end: "",
    },
    filters: {
      site_number: [],
      is_fuel: true,
    }
  };
  
  dashboardSalesSub: Subscription;

  showSiteFilter = false;

  dashboardDateMode: string = 'MONTH';

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.showSiteFilter || !this.filterCard?.nativeElement) {
      return;
    }

    const clickedInside = this.filterCard.nativeElement.contains(event.target);
    if (!clickedInside) {
      this.showSiteFilter = false;
      this.cdr.detectChanges();
    }
  }

  constructor(
    private translate: TranslateService,
    private titleService: Title,
    private store: Store<AppState>,
    private fb: FormBuilder,
    private dashboardService: DashboardService,
    private formatter: FormatPipe, 
    private cdr: ChangeDetectorRef
  ) {
    this.titleService.setTitle(this.translate.instant("DashBoard.Details"));
    
    this.sitesList$ = this.store.pipe(select(sitesStateSitesList));
  }

  ngOnInit() {
    this.dashboardDateMode = localStorage.getItem("dashboardDateMode") || "MONTH";

    this.loadLangChanges();
    this.setSiteDataForm();
    this.setSiteSearchSubscription();
  }

  setShowSiteFilter() {
    if (!this.showSiteFilter) {
      setTimeout(() => {
        this.showSiteFilter = true;
        this.cdr.detectChanges();
      }, 0);
    } else {
      this.showSiteFilter = false;
      this.cdr.detectChanges();
    }
  }

  loadLangChanges() {
    this.langSubscription = this.translate.onLangChange.subscribe(() => {
      this.titleService.setTitle(this.translate.instant("DashBoard.Details"));
    });
  }

  setSiteDataForm() {
    this.siteDataForm = this.fb.group({
      sites: this.fb.array([])
    });
  }

  async getDashboardData() {
    this.isLoadingChart1$.next(true);
    this.isLoadingChart2$.next(true);
    this.isLoadingChart3$.next(true);
    this.isLoadingChart4$.next(true);
    this.isLoadingChart5$.next(true);
    this.isLoadingChart6$.next(true);

    if (!this.echarts5) {
      await this.importEcharts();
    }
  
    // Build all requests with shareReplay to reuse them
    const req1$ = this.createDashboardRequest(RetailDashboardTypes.TOP_10_PRODUCTS, true).pipe(shareReplay(1));
    const req2$ = this.createDashboardRequest(RetailDashboardTypes.TOP_10_PRODUCTS, false).pipe(shareReplay(1));
    const req3$ = this.createDashboardRequest(RetailDashboardTypes.MOP).pipe(shareReplay(1));
    const req4$ = this.createDashboardRequest(RetailDashboardTypes.TOP_5_PRODUCTS_AND_REST, true).pipe(shareReplay(1));
    const req5$ = this.createDashboardRequest(RetailDashboardTypes.TOP_5_PRODUCTS_AND_REST, false).pipe(shareReplay(1));
    const req6$ = this.createDashboardRequest(RetailDashboardTypes.PRODUCTS_BY_SITE_IN_TIMESERIES, true).pipe(shareReplay(1));
  
    // Individual subscriptions for loading + chart rendering
    req1$.pipe(
      tap((series) => {
        this.chart1series = series;
        this.setBarChart(series, 'chart1', 'bar');
      }),
      finalize(() => this.isLoadingChart1$.next(false))
    ).subscribe();
    req2$.pipe(
      tap((series) => {
        this.chart2series = series;
        this.setBarChart(series, 'chart2', 'bar');
      }),
      finalize(() => this.isLoadingChart2$.next(false))
    ).subscribe();
    req3$.pipe(
      tap((series) => {
        this.chart3series = series;
        this.setPieChart(series, 'chart3', 'pie');
      }),
      finalize(() => this.isLoadingChart3$.next(false))
    ).subscribe();
    req4$.pipe(
      tap((series) => {
        this.setHorizontalStackedBarChart(series, 'chart4');
      }),
      finalize(() => this.isLoadingChart4$.next(false))
    ).subscribe();
    req5$.pipe(
      tap((series) => {
        this.setHorizontalStackedBarChart(series, 'chart5');
      }),
      finalize(() => this.isLoadingChart5$.next(false))
    ).subscribe();
    req6$.pipe(
      tap((series) => {
        this.setTimeSeriesChart(series);
      }),
      finalize(() => this.isLoadingChart6$.next(false))
    ).subscribe();
  
    // Fallback: just once, using shared observables
    forkJoin([req1$, req2$, req3$, req4$, req5$, req6$]).subscribe((allSeries) => {
      this.firstLoad = false;
    });
  }

  async importEcharts() {
    try {
      // Dynamically import ECharts 5
      this.echarts5 = await import(
        "../../../../libs/echarts5/node_modules/echarts"
      );
      
      // Add resize listener once all charts are initialized
      window.addEventListener("resize", this.onWindowResize);

    } catch (error) {
      console.error("Error loading ECharts 5:", error);
    }
  }

  createDashboardRequest(type: string, isFuel: boolean = false) {
    const treatedPayload = {
      ...this.dashboardPayload,
      type,
      filters: {
        ...this.dashboardPayload.filters,
        is_fuel: isFuel
      }
    };
  
    return this.dashboardService.getDashboardSales(treatedPayload).pipe(
      map(result => result.series),
      catchError((err) => {
        this.store.dispatch(
          setSnackbar(
            SnackBarUtils.setSnackBar(
              this.translate.instant("DashBoard.loadSalesDashboardError"),
              SnackbarType.ERROR
            )
          )
        );
        return of([]);
      }),
      shareReplay(1)
    );
  }

  onWindowResize = () => {
    this.chart1?.resize();
    this.chart2?.resize();
    this.chart3?.resize();
    this.chart4?.resize();
    this.chart5?.resize();
    this.chart6?.resize();
  };

  changeChartType(chart: string, type: string): void {
    const config = this.chartConfig[chart];
    if (!config) return;
  
    // Reset all status booleans for this chart
    Object.keys(config.status).forEach(key => config.status[key](false));
  
    // Set the selected type as active
    if (config.status[type]) {
      config.status[type](true);
    }
  
    // Call the appropriate chart setter
    const series = config.series();
    if (type === 'bar' || type === 'horizontalBar') {
      this.setBarChart(series, chart, type);
    } else if (type === 'pie' || type === 'doughnut') {
      this.setPieChart(series, chart, type);
    }
  }
  
  setBarChart(series: any, chart: string, type: string) {
    if (chart === 'chart1' && this.chart1) {
      this.chart1.dispose();
      this.chart1 = null;
    }

    if (chart === 'chart2' && this.chart2) {
      this.chart2.dispose();
      this.chart2 = null;
    }

    if (chart === 'chart3' && this.chart3) {
      this.chart3.dispose();
      this.chart3 = null;
    }

    switch (chart) {
      case "chart1":
        if (!this.chart1 && this.chartContainer1?.nativeElement) {
          this.chart1 = this.echarts5.init(this.chartContainer1.nativeElement);
        }
        break;
      case "chart2":
        if (!this.chart2 && this.chartContainer2?.nativeElement) {
          this.chart2 = this.echarts5.init(this.chartContainer2.nativeElement);
        }
        break;
      case "chart3":
        if (!this.chart3 && this.chartContainer3?.nativeElement) {
          this.chart3 = this.echarts5.init(this.chartContainer3.nativeElement);
        }
        break;
      default:
        break;
    }

    const chartRef = chart === 'chart1' ? this.chart1 : chart === 'chart2' ? this.chart2 : this.chart3;

    if (!series || series.length === 0) {
      const translationKey = chart === 'chart1' ? 'DashBoard.fuelProducts' : chart === 'chart2' ? 'DashBoard.nonFuelProducts' : this.translate.instant('DashBoard.usedPaymentModes');
      const subtextKey = chart === 'chart2' ? 'DashBoard.top10' : '';
      const emptyOption = this.setEmptyOptionBarChart(translationKey, subtextKey);
      chartRef.setOption(emptyOption, true);
      return;
    }
    
    const option = {
      title: {
        text: 
          chart === 'chart1' 
            ? this.translate.instant('DashBoard.fuelProducts') 
            : chart === 'chart2' 
            ? this.translate.instant('DashBoard.nonFuelProducts') 
            : this.translate.instant('DashBoard.usedPaymentModes'),
        subtext: chart === 'chart2' ? this.translate.instant('DashBoard.top10') : '',
        left: 'center'
      },
      tooltip: {
        trigger: 'item',
        formatter: (params) => {
          const decimals = this.tenantDataFormat.amountDecimalPlaces;
          const currencySymbol = this.tenantDataFormat.currency.symbol;
          return `${params.marker}${params.name}: <b>${Number(params.value).toFixed(decimals)} ${currencySymbol}</b>`;
        },
      },
      legend: {
        show: false
      },
      xAxis: {
        type: type === 'bar' ? 'category' : 'value',
        axisLabel: {
          overflow: 'truncate',
          showMaxLabel: true
        },
        data: type === 'bar' ? series.map(item => item.name) : null,
      },
      yAxis: {
        type: type === 'horizontalBar' ? 'category' : 'value',
        data: type === 'horizontalBar' ? series.map(item => item.name) : null,
      },
      series: [
        {
          name: this.translate.instant('Products.product'),
          type: 'bar',
          data: series.map(item => item.value),
          label: {
            show: true,
            position: type === 'bar' ? 'top' : 'right',
            formatter: (params) => Number(params.value).toFixed(this.tenantDataFormat.amountDecimalPlaces) + ' ' + this.tenantDataFormat.currency.symbol,
          },
          labelLayout: {
            hideOverlap: true
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          }
        }
      ]
    };

    chartRef.setOption(option, true);
  }

  setEmptyOptionBarChart(textTranslateKey: string, subtextTranslateKey?: string) {
    return { 
      title: {
        text: this.translate.instant(textTranslateKey),
        subtext: subtextTranslateKey ? this.translate.instant(subtextTranslateKey) : null,
        left: 'center'
      },
      tooltip: {},
      legend: {
        show: false
      },
      xAxis: {
        type: 'value'
      },
      yAxis: {
        type: 'category',
        data: ['']
      },
      series: [
        {
          type: 'bar',
          data: [0],
          barGap: '-100%',
          itemStyle: { opacity: 0 }
        }
      ],
      graphic: [
        {
          type: 'text',
          left: 'center',
          top: 'middle',
          style: {
            text: this.translate.instant('DashBoard.noDataAvailable'),
            fontSize: 16,
            fill: '#888'
          }
        }
      ]
    };
  }

  setPieChart(series: any, chart: string, type: string) {
    if (chart === 'chart1' && this.chart1) {
      this.chart1.dispose();
      this.chart1 = null;
    }

    if (chart === 'chart2' && this.chart2) {
      this.chart2.dispose();
      this.chart2 = null;
    }

    switch (chart) {
      case "chart1":
        if (!this.chart1 && this.chartContainer1?.nativeElement) {
          this.chart1 = this.echarts5.init(this.chartContainer1.nativeElement);
        }
        break;
      case "chart2":
        if (!this.chart2 && this.chartContainer2?.nativeElement) {
          this.chart2 = this.echarts5.init(this.chartContainer2.nativeElement);
        }
        break;
      case "chart3":
        if (!this.chart3 && this.chartContainer3?.nativeElement) {
          this.chart3 = this.echarts5.init(this.chartContainer3.nativeElement);
        }
        break;
      default:
        break;
    }

    const chartRef = chart === 'chart1' ? this.chart1 : chart === 'chart2' ? this.chart2 : this.chart3;

    if (!series || series.length === 0) {
      const emptyOption = {
        title: {
          text: 
            chart === 'chart1' 
              ? this.translate.instant('DashBoard.fuelProducts') 
              : chart === 'chart2' 
              ? this.translate.instant('DashBoard.nonFuelProducts') 
              : this.translate.instant('DashBoard.usedPaymentModes'),
            subtext: chart === 'chart2' ? this.translate.instant('DashBoard.top10') : '',
          left: 'center'
        },
        tooltip: {
          trigger: 'item',
        },
        legend: {
          show: false
        },
        series: [
          {
            name: 'No Data',
            type: 'pie',
            radius: type === 'doughnut' ? ['40%', '70%'] : '50%',
            data: [],
            label: {
              show: false
            }
          }
        ],
        graphic: [
          {
            type: 'text',
            left: 'center',
            top: 'middle',
            style: {
              text: this.translate.instant('DashBoard.noDataAvailable'),
              fontSize: 16,
              fill: '#888'
            }
          }
        ]
      };
    
      chartRef.setOption(emptyOption, true);
      return;
    }

    const option = {
      title: {
        text: 
          chart === 'chart1' ? this.translate.instant('DashBoard.fuelProducts') 
            : chart === 'chart2' ? this.translate.instant('DashBoard.nonFuelProducts') 
            : this.translate.instant('DashBoard.usedPaymentModes'),
          subtext: chart === 'chart2' ? this.translate.instant('DashBoard.top10') : '',
        left: 'center'  
      },
      tooltip: {
        trigger: "item",
        formatter: (params) => {
          const decimals = this.tenantDataFormat.amountDecimalPlaces;
          const currencySymbol = this.tenantDataFormat.currency.symbol;
          const value = Number(params.value).toFixed(decimals);
          const percent = Number(params.percent).toFixed(1);
          return `${params.marker}${params.name}: <b>${value} ${currencySymbol}</b> (${percent}%)`;
        }
      },
      legend: {
        type: 'scroll',
        orient: 'horizontal',
        bottom: 0,
        height: 40,
        pageIconSize: 10,
        pageButtonItemGap: 5
      },
      series: [
        {
          name: this.translate.instant('PosMovements.paymentMode'),
          type: "pie",
          radius: type === 'doughnut' ? ['40%', '70%'] : '50%',
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          },
          data: series,
        },
      ],
    };

    chartRef.setOption(option, true);
  }

  setHorizontalStackedBarChart(series: any, chart: string) {
    if (chart === 'chart4' && this.chart4) {
      this.chart4.dispose();
      this.chart4 = null;
    }

    if (chart === 'chart5' && this.chart5) {
      this.chart5.dispose();
      this.chart5 = null;
    }

    switch (chart) {
      case "chart4":
        if (!this.chart4 && this.chartContainer4?.nativeElement) {
          this.chart4 = this.echarts5.init(this.chartContainer4.nativeElement);
        }
        break;
      case "chart5":
        if (!this.chart5 && this.chartContainer5?.nativeElement) {
          this.chart5 = this.echarts5.init(this.chartContainer5.nativeElement);
        }
        break;
      default:
        break;
    }

    const chartRef = chart === 'chart4' ? this.chart4 : this.chart5;

    if (!series || series.length === 0) {
      const translationKey = chart === 'chart4' ? 'DashBoard.fuelProductsBySite' : 'DashBoard.nonFuelProductsBySite';
      const emptyOption = this.setEmptyOptionBarChart(translationKey, 'DashBoard.top5');
      chartRef.setOption(emptyOption, true);
      return;
    }

    const decimals = this.tenantDataFormat.amountDecimalPlaces;
    const currencySymbol = this.tenantDataFormat.currency.symbol;

    const treatedSeries = series.map((item) => ({ 
      name: item.group,
      type: 'bar',
      stack: 'total',
      labelLayout: {
        hideOverlap: true,
      },
      label: {
        show: true,
        formatter: (params) => {
          const value = Number(params.value[0]);
          if (value >= 1000000) return (value / 1000000).toFixed(decimals) + 'M' + ' ' + currencySymbol;
          if (value >= 1000) return (value / 1000).toFixed(decimals) + 'k' + ' ' + currencySymbol;
          return value + ' ' + currencySymbol;
        }
      },
      emphasis: {
        focus: 'series'
      },
      data: item.data,
    }));

    const option = {
      title: {
        text: chart === 'chart4' ? this.translate.instant('DashBoard.fuelProductsBySite') : this.translate.instant('DashBoard.nonFuelProductsBySite'),
        subtext: this.translate.instant('DashBoard.top5'),
        left: 'center'
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        formatter: (params) => {
          return params.map(p => {
            const value = Number(p.value[0]).toFixed(decimals);
            return `${p.marker}${p.seriesName}: <b>${value} ${currencySymbol}</b>`;
          }).join('<br/>');
        },
      },
      legend: {
        type: 'scroll',
        orient: 'horizontal',
        bottom: 0,
        height: 40,
        pageIconSize: 10,
        pageButtonItemGap: 5
      },
      xAxis: {
        type: 'value'
      },
      yAxis: {
        type: 'category'
      },
      series: treatedSeries,
    };
    
    chartRef.setOption(option, true);
  }

  setTimeSeriesChart(series: any) {  
    if (!this.chart6 && this.chartContainer6?.nativeElement) {
      this.chart6 = this.echarts5.init(this.chartContainer6.nativeElement);
    }
  
    if (!series || series.length === 0) {
      const emptyOption = {
        title: {
          text: this.translate.instant('DashBoard.salesAmountGroupedByHour'),
          subtext: this.translate.instant('DashBoard.fuel'),
          left: 'center'
        },
        xAxis: { type: 'time' },
        yAxis: { type: 'value' },
        series: [
          {
            type: 'line',
            data: [],
            smooth: true
          }
        ],
        graphic: {
          type: 'text',
          left: 'center',
          top: 'middle',
          style: {
            text: this.translate.instant('DashBoard.noDataAvailable'),
            fontSize: 16,
            fill: '#888'
          }
        }
      };

      this.chart6.setOption(emptyOption, true);
      return;
    }

    const baseDate = new Date();
    baseDate.setMinutes(0, 0, 0);

    const treatedSeries = series.map((item) => {
      const formattedData = item.data.map(([hourStr, value]) => {
        const hour = parseInt(hourStr, 10);
        const date = new Date(baseDate);
        date.setHours(hour);
    
        return [date, parseFloat(value)];
      });

      return {
        type: "line",
        smooth: true,
        name: item.group,
        data: formattedData,
      }
    });

    const option = {
      title: {
        text: this.translate.instant('DashBoard.salesAmountGroupedByHour'),
        subtext: this.translate.instant('DashBoard.fuel'),
        left: 'center'
      },
      legend: {
        bottom: 0,
        left: 'center',
      },
      xAxis: { 
        type: "time",
        axisLabel: {
          formatter: (value) => {
            const date = new Date(value);
            const hours = date.getHours().toString().padStart(2, '0');
            return `${hours}:00`;
          }
        },
        splitLine: {
          show: false
        },
      },
      yAxis: { type: "value" },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'line'
        },
        formatter: (params) => {
          const decimals = this.tenantDataFormat.amountDecimalPlaces;
          const currencySymbol = this.tenantDataFormat.currency.symbol;

          const timestamp = params[0].value[0];
          const date =  new Date(timestamp);
          const hours = date.getHours().toString().padStart(2, '0');
          const minutes = date.getMinutes().toString().padStart(2, '0');

          const timeLabel = `${hours}:${minutes}`;
    
          const lines = params.map(p => {
            const value = Number(p.value[1]).toFixed(decimals);
            return `${p.marker}${p.seriesName}: <b>${value} ${currencySymbol}</b>`;
          });

          return `${timeLabel}<br/>${lines.join('<br/>')}`;
        }
      },
      series: treatedSeries,
    };

    this.chart6.setOption(option, true);
  }

  setSelectedDateMode(mode: string) {
    this.dashboardDateMode = mode;
    localStorage.setItem("dashboardDateMode", mode);
  }  

  getDates(dates) {
    this.dashboardPayload = {
      ...this.dashboardPayload,
      range: {
        start: this.firstLoad && localStorage.getItem("dashboardStartDate") ? localStorage.getItem("dashboardStartDate") : dates.startDate,
        end: this.firstLoad && localStorage.getItem("dashboardEndDate") ? localStorage.getItem("dashboardEndDate") : dates.endDate, 
      },
    }

    if (!this.firstLoad) {
      localStorage.setItem("dashboardStartDate", dates.startDate);
      localStorage.setItem("dashboardEndDate", dates.endDate);
    }

    this.getDashboardData();
  }

  clearFormControl(formGroup: FormGroup, control: string) {
    formGroup.controls[control].setValue("");
  }

  getFormArrayValue() {
    return this.siteDataForm.get("sites").value;
  }

  selectSite(event: MatAutocompleteSelectedEvent, searchInput: HTMLInputElement) {
    const {
      option: { value: selectedOption },
    } = event;

    if (this.siteAlreadeSelected(selectedOption, this.getFormArrayValue())){
      return;
    };

    const formArray = this.siteDataForm.get("sites") as FormArray;
    formArray.push(this.fb.control(selectedOption));

    searchInput.value = "";

    this.setFilterSiteNumbers(formArray);
  }

  siteAlreadeSelected(selectedOption: AutocompleteSelectItem, options: AutocompleteSelectItem[]): boolean {
    if (
      !options ||
      !options.length ||
      !options.find((option) => option.code === selectedOption.code)
    ) {
      return false;
    }
    return true;
  }

  removeSite(option: {code: string, description: string}) {
    const formArray = this.siteDataForm.get("sites") as FormArray;
    const index = formArray.controls.findIndex(
      (ctrl: AbstractControl) => ctrl.value.code === option.code
    );
    if (index > -1) {
      formArray.removeAt(index);
    }

    this.setFilterSiteNumbers(formArray);
  }

  clearSites() {   
    const formArray = this.siteDataForm.get("sites") as FormArray;
    formArray.clear();

    this.setFilterSiteNumbers(formArray);
  }

  setFilterSiteNumbers(formArray: FormArray) {
    this.dashboardPayload = {
      ...this.dashboardPayload,
      filters: {
        ...this.dashboardPayload.filters,
        site_number: formArray.value.map((site) => site.displayCode),
      }
    }

    this.getDashboardData();
  }

  setSiteSearchSubscription() {
    this.sitesSub = this.sitesList$.pipe(skip(1)).subscribe((sites) => {
      if (sites) {
        this.sitesListData = sites.map((site) => {
          return {
            code: site.code,
            displayCode: site.siteNumber,
            description: site.description ? site.description : "",
          };
        });
      }
      this.loadingSearch = false;
    });
  }

  validateFirstSearch() {
    if (!this.sitesListData.length) {
      this.searchSites('');
    }
  }

  searchSites(event: KeyboardEvent | string, hasTimer = true): void {
    const searchTerm = typeof event === 'string' ? event : (event.target as HTMLInputElement).value;

    clearTimeout(this.searchTimer);

    this.searchTimer = window.setTimeout(() => {
      this.loadingSearch = true;
      this.store.dispatch(
        getSitesList({
          options: {
            filteringArgs: {
              filterConfig: [
                { field: "siteNumber" },
                { field: "description" },
              ],
              searchTerms: [searchTerm],
            },
          },
        })
      );
    }, hasTimer ? 500 : 0);
  }

  setSelectedBusinessType(businessType: BusinessType) {
    this.selectBusiness.emit(businessType);
  }

  ngOnDestroy(): void {
    window.removeEventListener("resize", this.onWindowResize);
    if (this.langSubscription) {
      this.langSubscription.unsubscribe();
    }
    if (this.sitesSub) {
      this.sitesSub.unsubscribe();
    }
  }
}
